import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../config/app-config';
import { AdminAgencia, AdminBootstrapResponse, AdminCatalogSnapshot, AdminSubagencia, AdminZona, AgencyMutationResponse, AgencyPayload, CatalogKey, CatalogMutationResult, CatalogRecord, PackageTypeMutationResponse, PackageTypeRecord, ProductTypeMutationResponse, ProductTypeRecord, ProgramMutationResponse, ProgramMutationResult, ProgramPayload, SubagencyMutationResponse, SubagencyPayload, ZoneMutationResponse, ZonePayload } from '../../models/api/admin-catalogs';
import { ApiClientError } from '../../models/api/api-envelope';
import { buildApiUrl } from '../api-http/form-url-encoded';
import { mapAdminBootstrapResponse, mapAgencyMutationResponse, mapPackageTypeMutationResponse, mapProductTypeMutationResponse, mapProgramMutationResponse, mapSubagencyMutationResponse, mapZoneMutationResponse, normalizeCatalogId } from './catalogs-mapper';
import { normalizeProductTypeName, ProductTypeError } from '../../utils/product-type-form';
import { normalizePackageTypeName, normalizePackageTypeStatus } from '../../utils/package-type-form';
import { normalizeProgramName, normalizeProgramStatus } from '../../utils/program-form';
import { normalizeAgencyName, normalizeSubagencyName, normalizeZoneName, normalizeZoneStatus } from '../../utils/administrative-catalog-form';

@Injectable()
export class CatalogsProvider {
  private current: AdminCatalogSnapshot = null;
  private generation = 0;
  private activeWrite = 0;
  private nextWrite = 0;
  private clearHandlers: Array<() => void> = [];
  private loadSequence = 0;

  constructor(private http: HttpClient) {}

  load(apiKey: string, locationId: number | string): Promise<AdminCatalogSnapshot> {
    return this.requestSnapshot(apiKey, locationId, false);
  }

  refresh(apiKey: string, locationId: number | string): Promise<AdminCatalogSnapshot> {
    return this.requestSnapshot(apiKey, locationId, true);
  }

  private requestSnapshot(apiKey: string, locationId: number | string,
    force: boolean): Promise<AdminCatalogSnapshot> {
    const normalizedLocationId = normalizeCatalogId(locationId);
    if (!force && this.current && this.current.selectedLocationId === normalizedLocationId) {
      return Promise.resolve(this.current);
    }
    if (this.current && this.current.selectedLocationId !== normalizedLocationId) {
      this.generation++;
      this.activeWrite = 0;
      this.current = null;
      this.notifyClearHandlers();
    }
    const sequence = ++this.loadSequence;
    const requestGeneration = this.generation;
    const headers = new HttpHeaders({ 'Authorization': apiKey });
    const request = this.http.get<AdminBootstrapResponse>(
      buildApiUrl(APP_CONFIG.apiBaseUrl, 'catalogo-local/admin-bootstrap?idlocacion=' +
        encodeURIComponent(String(normalizedLocationId))),
      { headers: headers, observe: 'response' }
    ).toPromise();

    return request.then(response => {
      if (sequence !== this.loadSequence || requestGeneration !== this.generation) {
        throw new ProductTypeError('stale', 'La locación cambió durante la carga.');
      }
      return this.acceptResponse(response, normalizedLocationId);
    })
      .catch((error: any) => {
        if (error instanceof ApiClientError || error instanceof ProductTypeError) {
          return Promise.reject(error);
        }
        return Promise.reject(new ApiClientError('network', 'No fue posible obtener los catálogos.'));
      });
  }

  getCatalog(key: CatalogKey): CatalogRecord[] {
    return this.current ? this.current.catalogs[key].slice() : [];
  }

  onClear(handler: () => void): () => void {
    this.clearHandlers.push(handler);
    return () => {
      this.clearHandlers = this.clearHandlers.filter(function (candidate) { return candidate !== handler; });
    };
  }

  createProductType(apiKey: string, locationId: number | string, name: string): Promise<ProductTypeRecord> {
    return this.mutateProductType('post', apiKey, locationId, null, name);
  }

  updateProductType(apiKey: string, locationId: number | string, id: number | string,
    name: string): Promise<ProductTypeRecord> {
    return this.mutateProductType('put', apiKey, locationId, id, name);
  }

  createPackageType(apiKey: string, locationId: number | string, name: string,
    status: number): Promise<PackageTypeRecord> {
    return this.mutatePackageType('post', apiKey, locationId, null, name, status);
  }

  updatePackageType(apiKey: string, locationId: number | string, id: number | string,
    name: string, status: number): Promise<PackageTypeRecord> {
    return this.mutatePackageType('put', apiKey, locationId, id, name, status);
  }

  createProgram(apiKey: string, locationId: number | string,
    payload: ProgramPayload): Promise<ProgramMutationResult> {
    return this.mutateProgram('post', apiKey, locationId, null, payload);
  }

  updateProgram(apiKey: string, locationId: number | string, id: number | string,
    payload: ProgramPayload): Promise<ProgramMutationResult> {
    return this.mutateProgram('put', apiKey, locationId, id, payload);
  }

  createAgency(apiKey: string, locationId: number | string,
    payload: AgencyPayload): Promise<CatalogMutationResult<AdminAgencia>> {
    return this.mutateAgency('post', apiKey, locationId, null, payload);
  }

  updateAgency(apiKey: string, locationId: number | string, agencyId: number | string,
    payload: AgencyPayload): Promise<CatalogMutationResult<AdminAgencia>> {
    return this.mutateAgency('put', apiKey, locationId, agencyId, payload);
  }

  createSubagency(apiKey: string, locationId: number | string, agencyId: number | string,
    payload: SubagencyPayload): Promise<CatalogMutationResult<AdminSubagencia>> {
    return this.mutateSubagency('post', apiKey, locationId, agencyId, null, payload);
  }

  updateSubagency(apiKey: string, locationId: number | string, agencyId: number | string,
    subagencyId: number | string, payload: SubagencyPayload): Promise<CatalogMutationResult<AdminSubagencia>> {
    return this.mutateSubagency('put', apiKey, locationId, agencyId, subagencyId, payload);
  }

  createZone(apiKey: string, locationId: number | string,
    payload: ZonePayload): Promise<CatalogMutationResult<AdminZona>> {
    return this.mutateZone('post', apiKey, locationId, null, payload);
  }

  updateZone(apiKey: string, locationId: number | string, zoneId: number | string,
    payload: ZonePayload): Promise<CatalogMutationResult<AdminZona>> {
    return this.mutateZone('put', apiKey, locationId, zoneId, payload);
  }

  clear(): void {
    this.generation++;
    this.loadSequence++;
    this.activeWrite = 0;
    this.current = null;
    this.notifyClearHandlers();
  }

  private mutateProductType(method: 'post' | 'put', apiKey: string, locationId: number | string,
    id: number | string, name: string): Promise<ProductTypeRecord> {
    if (typeof apiKey !== 'string' || !apiKey.trim()) {
      return Promise.reject(new ProductTypeError('session', 'La sesión ya no está disponible.'));
    }
    let normalizedLocationId: number;
    let normalizedName: string;
    let normalizedId: number = null;
    try {
      normalizedLocationId = normalizeCatalogId(locationId);
      normalizedName = normalizeProductTypeName(name);
      if (method === 'put') { normalizedId = normalizeCatalogId(id); }
    } catch (error) {
      return Promise.reject(error instanceof ProductTypeError ? error :
        new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (!this.current || this.current.selectedLocationId !== normalizedLocationId) {
      return Promise.reject(new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (this.activeWrite) {
      return Promise.reject(new ProductTypeError('unavailable', 'Ya hay una operación en curso.'));
    }
    const token = ++this.nextWrite;
    const requestGeneration = this.generation;
    this.activeWrite = token;
    const path = 'catalogo-local/admin/tipos-producto' + (method === 'put' ? '/' + normalizedId : '') +
      '?idlocacion=' + encodeURIComponent(String(normalizedLocationId));
    const options = {
      headers: new HttpHeaders({ 'Authorization': apiKey, 'Content-Type': 'application/json' }),
      observe: 'response' as 'response'
    };
    const body = { tipoproducto: normalizedName };
    const observable: any = method === 'post' ?
      this.http.post<ProductTypeMutationResponse>(buildApiUrl(APP_CONFIG.apiBaseUrl, path), body, options) :
      this.http.put<ProductTypeMutationResponse>(buildApiUrl(APP_CONFIG.apiBaseUrl, path), body, options);

    return observable.toPromise().then((response: HttpResponse<ProductTypeMutationResponse>) => {
      if (requestGeneration !== this.generation) {
        throw new ProductTypeError('stale', 'La sesión cambió durante la operación.');
      }
      if (response.status === 401 || response.status === 403) {
        throw new ProductTypeError('unauthorized', 'La sesión no está autorizada para esta operación.');
      }
      if (response.status !== 200) {
        throw new ProductTypeError('invalid-response', 'La respuesta del servidor no es válida.');
      }
      if (response.body && response.body.code === '1') {
        throw this.businessError(response.body.message);
      }
      const record = mapProductTypeMutationResponse(response.body, normalizedLocationId);
      this.reconcileProductType(record, normalizedLocationId);
      return record;
    }).catch((error: any) => {
      if (error instanceof ProductTypeError || error instanceof ApiClientError) {
        return Promise.reject(error);
      }
      if (error && (error.status === 401 || error.status === 403)) {
        return Promise.reject(new ProductTypeError('unauthorized', 'La sesión no está autorizada para esta operación.'));
      }
      return Promise.reject(new ProductTypeError('network', 'No fue posible guardar el tipo de producto.'));
    }).then((record: ProductTypeRecord) => {
      if (this.activeWrite === token) { this.activeWrite = 0; }
      return record;
    }, (error: any) => {
      if (this.activeWrite === token) { this.activeWrite = 0; }
      return Promise.reject(error);
    });
  }

  private businessError(message: string): ProductTypeError {
    const serverMessage = typeof message === 'string' ? message.trim() : '';
    if (/existe|duplicad/i.test(serverMessage)) {
      return new ProductTypeError('duplicate', 'Ya existe un tipo de producto con ese nombre.');
    }
    if (/no tuvo cambios/i.test(serverMessage)) {
      return new ProductTypeError('unchanged',
        'El nombre enviado ya es el que tiene guardado este tipo de producto.');
    }
    return new ProductTypeError('unavailable', serverMessage ||
      'No fue posible guardar el tipo de producto.');
  }

  private mutatePackageType(method: 'post' | 'put', apiKey: string, locationId: number | string,
    id: number | string, name: string, status: number): Promise<PackageTypeRecord> {
    if (typeof apiKey !== 'string' || !apiKey.trim()) {
      return Promise.reject(new ProductTypeError('session', 'La sesión ya no está disponible.'));
    }
    let normalizedLocationId: number;
    let normalizedName: string;
    let normalizedStatus: 1 | 2;
    let normalizedId: number = null;
    try {
      normalizedLocationId = normalizeCatalogId(locationId);
      normalizedName = normalizePackageTypeName(name);
      normalizedStatus = normalizePackageTypeStatus(status);
      if (method === 'put') { normalizedId = normalizeCatalogId(id); }
    } catch (error) {
      return Promise.reject(error instanceof ProductTypeError ? error :
        new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (!this.current || this.current.selectedLocationId !== normalizedLocationId) {
      return Promise.reject(new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (this.activeWrite) {
      return Promise.reject(new ProductTypeError('unavailable', 'Ya hay una operación en curso.'));
    }
    const token = ++this.nextWrite;
    const requestGeneration = this.generation;
    this.activeWrite = token;
    const path = 'catalogo-local/admin/tipos-paquete' + (method === 'put' ? '/' + normalizedId : '') +
      '?idlocacion=' + encodeURIComponent(String(normalizedLocationId));
    const options = {
      headers: new HttpHeaders({ 'Authorization': apiKey, 'Content-Type': 'application/json' }),
      observe: 'response' as 'response'
    };
    const body = { tipopaquete: normalizedName, idestatus: normalizedStatus };
    const observable: any = method === 'post' ?
      this.http.post<PackageTypeMutationResponse>(buildApiUrl(APP_CONFIG.apiBaseUrl, path), body, options) :
      this.http.put<PackageTypeMutationResponse>(buildApiUrl(APP_CONFIG.apiBaseUrl, path), body, options);

    return observable.toPromise().then((response: HttpResponse<PackageTypeMutationResponse>) => {
      if (requestGeneration !== this.generation) {
        throw new ProductTypeError('stale', 'La sesión cambió durante la operación.');
      }
      if (response.status === 401 || response.status === 403) {
        throw new ProductTypeError('unauthorized', 'La sesión no está autorizada para esta operación.');
      }
      if (response.status !== 200) {
        throw new ProductTypeError('invalid-response', 'La respuesta del servidor no es válida.');
      }
      if (response.body && response.body.code === '1') {
        throw this.packageBusinessError(response.body.message);
      }
      const record = mapPackageTypeMutationResponse(response.body, normalizedLocationId);
      this.reconcilePackageType(record, normalizedLocationId);
      return record;
    }).catch((error: any) => {
      if (error instanceof ProductTypeError || error instanceof ApiClientError) {
        return Promise.reject(error);
      }
      if (error && (error.status === 401 || error.status === 403)) {
        return Promise.reject(new ProductTypeError('unauthorized', 'La sesión no está autorizada para esta operación.'));
      }
      return Promise.reject(new ProductTypeError('network', 'No fue posible guardar el tipo de paquete.'));
    }).then((record: PackageTypeRecord) => {
      if (this.activeWrite === token) { this.activeWrite = 0; }
      return record;
    }, (error: any) => {
      if (this.activeWrite === token) { this.activeWrite = 0; }
      return Promise.reject(error);
    });
  }

  private packageBusinessError(message: string): ProductTypeError {
    const serverMessage = typeof message === 'string' ? message.trim() : '';
    if (/existe|duplicad/i.test(serverMessage)) {
      return new ProductTypeError('duplicate', 'Ya existe un tipo de paquete con ese nombre.');
    }
    if (/no tuvo cambios/i.test(serverMessage)) {
      return new ProductTypeError('unchanged',
        'El nombre y el estado enviados ya son los que tiene guardado este tipo de paquete.');
    }
    return new ProductTypeError('unavailable', serverMessage ||
      'No fue posible guardar el tipo de paquete.');
  }

  private reconcilePackageType(record: PackageTypeRecord, locationId: number): void {
    if (!this.current || this.current.selectedLocationId !== locationId) { return; }
    const records = this.current.catalogs.tiposPaquete.filter(function (item) {
      return item.idtipopaquete !== record.idtipopaquete;
    });
    records.push(Object.assign({}, record));
    records.sort(function (left, right) {
      return String(left.tipopaquete || '').localeCompare(String(right.tipopaquete || ''), 'es', { sensitivity: 'base' });
    });
    this.current.catalogs.tiposPaquete = records;
  }

  private mutateProgram(method: 'post' | 'put', apiKey: string, locationId: number | string,
    id: number | string, payload: ProgramPayload): Promise<ProgramMutationResult> {
    if (typeof apiKey !== 'string' || !apiKey.trim()) {
      return Promise.reject(new ProductTypeError('session', 'La sesión ya no está disponible.'));
    }
    let normalizedLocationId: number;
    let normalizedId: number = null;
    let body: ProgramPayload;
    try {
      normalizedLocationId = normalizeCatalogId(locationId);
      body = {
        programa: normalizeProgramName(payload && payload.programa),
        idestatus: normalizeProgramStatus(payload && payload.idestatus)
      };
      if (method === 'put') { normalizedId = normalizeCatalogId(id); }
    } catch (error) {
      return Promise.reject(error instanceof ProductTypeError ? error :
        new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (!this.current || this.current.selectedLocationId !== normalizedLocationId) {
      return Promise.reject(new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (this.activeWrite) {
      return Promise.reject(new ProductTypeError('unavailable', 'Ya hay una operación en curso.'));
    }
    const token = ++this.nextWrite;
    const requestGeneration = this.generation;
    this.activeWrite = token;
    const path = 'catalogo-local/admin/programas' + (method === 'put' ? '/' + normalizedId : '') +
      '?idlocacion=' + encodeURIComponent(String(normalizedLocationId));
    const options = {
      headers: new HttpHeaders({ 'Authorization': apiKey, 'Content-Type': 'application/json' }),
      observe: 'response' as 'response'
    };
    const observable = method === 'post' ?
      this.http.post<ProgramMutationResponse>(buildApiUrl(APP_CONFIG.apiBaseUrl, path), body, options) :
      this.http.put<ProgramMutationResponse>(buildApiUrl(APP_CONFIG.apiBaseUrl, path), body, options);

    return observable.toPromise().then((response: HttpResponse<ProgramMutationResponse>) => {
      if (requestGeneration !== this.generation) {
        throw new ProductTypeError('stale', 'La sesión cambió durante la operación.');
      }
      if (response.status !== 200) {
        throw new ProductTypeError('invalid-response', 'La respuesta del servidor no es válida.');
      }
      if (response.body && response.body.code === '1') {
        throw this.programBusinessError(response.body.message);
      }
      const record = mapProgramMutationResponse(response.body, normalizedLocationId);
      return this.refresh(apiKey, normalizedLocationId).then(snapshot => {
        const persisted = snapshot.catalogs.programas.some(function (program) {
          return program.idprograma === record.idprograma && program.idlocacion === record.idlocacion &&
            program.programa === record.programa && program.idestatus === record.idestatus;
        });
        if (!persisted) {
          throw new ProductTypeError('invalid-response', 'No fue posible confirmar el programa guardado.');
        }
        return { record: record, snapshot: snapshot };
      });
    }).catch(error => {
      if (error instanceof ProductTypeError || error instanceof ApiClientError) {
        return Promise.reject(error);
      }
      if (error && (error.status === 401 || error.status === 403)) {
        return Promise.reject(new ProductTypeError('unauthorized', 'No tienes acceso a esta locación.'));
      }
      return Promise.reject(new ProductTypeError('network', 'No fue posible guardar el programa.'));
    }).then((result: ProgramMutationResult) => {
      if (this.activeWrite === token) { this.activeWrite = 0; }
      return result;
    }, error => {
      if (this.activeWrite === token) { this.activeWrite = 0; }
      return Promise.reject(error);
    });
  }

  private programBusinessError(message: string): ProductTypeError {
    if (/existe|duplicad/i.test(message || '')) {
      return new ProductTypeError('duplicate', 'Ya existe un programa con ese nombre.');
    }
    if (/acceso|autorizad|locación|locacion/i.test(message || '')) {
      return new ProductTypeError('unauthorized', 'No tienes acceso a esta locación.');
    }
    if (/no encontrad|ya no existe/i.test(message || '')) {
      return new ProductTypeError('unavailable', 'El programa ya no existe.');
    }
    return new ProductTypeError('unavailable', 'No fue posible guardar el programa.');
  }

  private mutateAgency(method: 'post' | 'put', apiKey: string, locationId: number | string,
    agencyId: number | string, payload: AgencyPayload): Promise<CatalogMutationResult<AdminAgencia>> {
    let name: string;
    try { name = normalizeAgencyName(payload && payload.agencia); } catch (error) { return Promise.reject(error); }
    let id: number = null;
    try { if (method === 'put') { id = normalizeCatalogId(agencyId); } } catch (error) { return Promise.reject(error); }
    const path = 'catalogo-local/admin/agencias' + (method === 'put' ? '/' + id : '');
    return this.mutateAdministrative<AdminAgencia, AgencyMutationResponse>(method, apiKey, locationId, path,
      { agencia: name }, response => mapAgencyMutationResponse(response, locationId), 'agencia');
  }

  private mutateSubagency(method: 'post' | 'put', apiKey: string, locationId: number | string,
    agencyId: number | string, subagencyId: number | string,
    payload: SubagencyPayload): Promise<CatalogMutationResult<AdminSubagencia>> {
    let name: string;
    try { name = normalizeSubagencyName(payload && payload.subagencia); } catch (error) { return Promise.reject(error); }
    let parentId: number; let childId: number = null;
    try { parentId = normalizeCatalogId(agencyId); if (method === 'put') { childId = normalizeCatalogId(subagencyId); } }
    catch (error) { return Promise.reject(new ProductTypeError('validation', 'Selecciona una agencia.')); }
    const path = 'catalogo-local/admin/agencias/' + parentId + '/subagencias' +
      (method === 'put' ? '/' + childId : '');
    return this.mutateAdministrative<AdminSubagencia, SubagencyMutationResponse>(method, apiKey, locationId, path,
      { subagencia: name }, response => mapSubagencyMutationResponse(response, locationId, parentId), 'subagencia');
  }

  private mutateZone(method: 'post' | 'put', apiKey: string, locationId: number | string,
    zoneId: number | string, payload: ZonePayload): Promise<CatalogMutationResult<AdminZona>> {
    let body: ZonePayload;
    try { body = { zona: normalizeZoneName(payload && payload.zona),
      idestatus: normalizeZoneStatus(payload && payload.idestatus) }; } catch (error) { return Promise.reject(error); }
    let id: number = null;
    try { if (method === 'put') { id = normalizeCatalogId(zoneId); } } catch (error) { return Promise.reject(error); }
    const path = 'catalogo-local/admin/zonas' + (method === 'put' ? '/' + id : '');
    return this.mutateAdministrative<AdminZona, ZoneMutationResponse>(method, apiKey, locationId, path, body,
      response => mapZoneMutationResponse(response, locationId), 'zona');
  }

  private mutateAdministrative<T extends AdminAgencia | AdminSubagencia | AdminZona,
    R extends { code: string | number; message?: string }>(method: 'post' | 'put', apiKey: string,
    locationId: number | string, path: string, body: AgencyPayload | SubagencyPayload | ZonePayload,
    mapper: (response: R) => T,
    entity: 'agencia' | 'subagencia' | 'zona'): Promise<CatalogMutationResult<T>> {
    if (typeof apiKey !== 'string' || !apiKey.trim()) {
      return Promise.reject(new ProductTypeError('session', 'La sesión ya no es válida.'));
    }
    let normalizedLocationId: number;
    try { normalizedLocationId = normalizeCatalogId(locationId); } catch (error) {
      return Promise.reject(new ProductTypeError('location', 'Selecciona una locación válida.'));
    }
    if (!this.current || this.current.selectedLocationId !== normalizedLocationId) {
      return Promise.reject(new ProductTypeError('location', 'No tienes acceso a esta locación.'));
    }
    if (this.activeWrite) { return Promise.reject(new ProductTypeError('unavailable', 'Ya hay una operación en curso.')); }
    const token = ++this.nextWrite;
    const requestGeneration = this.generation;
    this.activeWrite = token;
    const url = buildApiUrl(APP_CONFIG.apiBaseUrl, path + '?idlocacion=' + encodeURIComponent(String(normalizedLocationId)));
    const options = { headers: new HttpHeaders({ 'Authorization': apiKey, 'Content-Type': 'application/json' }),
      observe: 'response' as 'response' };
    const observable: any = method === 'post' ? this.http.post<R>(url, body, options) : this.http.put<R>(url, body, options);
    return observable.toPromise().then((response: HttpResponse<R>) => {
      if (requestGeneration !== this.generation) { throw new ProductTypeError('stale', 'La sesión cambió durante la operación.'); }
      if (response.status === 401 || response.status === 403) {
        throw new ProductTypeError('unauthorized', 'No tienes acceso a esta locación.');
      }
      if (response.status !== 200) { throw new ProductTypeError('invalid-response', 'La respuesta del servidor no es válida.'); }
      const envelope = response.body;
      if (envelope && envelope.code === '1') { throw this.administrativeBusinessError(entity, envelope.message); }
      const record = mapper(response.body);
      return this.refresh(apiKey, normalizedLocationId).then(snapshot => {
        if (!this.administrativeRecordExists(snapshot, entity, record)) {
          throw new ProductTypeError('invalid-response', 'No fue posible confirmar el registro guardado.');
        }
        return { record: record, snapshot: snapshot };
      });
    }).catch(error => {
      if (error instanceof ProductTypeError || error instanceof ApiClientError) { return Promise.reject(error); }
      if (error && (error.status === 401 || error.status === 403)) {
        return Promise.reject(new ProductTypeError('unauthorized', 'No tienes acceso a esta locación.'));
      }
      return Promise.reject(new ProductTypeError('network', 'No fue posible guardar la ' + entity + '.'));
    }).then(result => { if (this.activeWrite === token) { this.activeWrite = 0; } return result; }, error => {
      if (this.activeWrite === token) { this.activeWrite = 0; } return Promise.reject(error);
    });
  }

  private administrativeBusinessError(entity: string, message: string): ProductTypeError {
    if (/existe|duplicad/i.test(message || '')) {
      return new ProductTypeError('duplicate', entity === 'subagencia' ?
        'Ya existe una subagencia con ese nombre en esta agencia.' : 'Ya existe una ' + entity + ' con ese nombre.');
    }
    if (/\bagencia\b.*(?:no encontr|no existe)/i.test(message || '')) {
      return new ProductTypeError('unavailable', 'La agencia ya no existe.');
    }
    if (/no encontr|no existe/i.test(message || '')) {
      return new ProductTypeError('unavailable', 'La ' + entity + ' ya no existe.');
    }
    return new ProductTypeError('unavailable', 'No fue posible guardar la ' + entity + '.');
  }

  private administrativeRecordExists(snapshot: AdminCatalogSnapshot, entity: string,
    record: AdminAgencia | AdminSubagencia | AdminZona): boolean {
    if (entity === 'agencia') {
      const agency = record as AdminAgencia;
      return snapshot.catalogs.agencias.some(function (item) {
        return item.idagencia === agency.idagencia && item.agencia === agency.agencia;
      });
    }
    if (entity === 'subagencia') {
      const subagency = record as AdminSubagencia;
      return snapshot.catalogs.subagencias.some(function (item) {
        return item.idagencia === subagency.idagencia && item.idsubagencia === subagency.idsubagencia &&
          item.subagencia === subagency.subagencia;
      });
    }
    const zone = record as AdminZona;
    return snapshot.catalogs.zonas.some(function (item) {
      return item.idzona === zone.idzona && item.zona === zone.zona && item.idestatus === zone.idestatus;
    });
  }

  private notifyClearHandlers(): void {
    this.clearHandlers.slice().forEach(function (handler) { handler(); });
  }

  private reconcileProductType(record: ProductTypeRecord, locationId: number): void {
    if (!this.current || this.current.selectedLocationId !== locationId) { return; }
    const records = this.current.catalogs.tiposProducto.filter(function (item) {
      return item.idtipoproducto !== record.idtipoproducto;
    });
    records.push(Object.assign({}, record));
    records.sort(function (left, right) {
      return String(left.tipoproducto || '').localeCompare(String(right.tipoproducto || ''), 'es', { sensitivity: 'base' });
    });
    this.current.catalogs.tiposProducto = records;
  }

  private acceptResponse(response: HttpResponse<AdminBootstrapResponse>,
    locationId: number): AdminCatalogSnapshot {
    if (response.status !== 200) {
      throw new ApiClientError('invalid-response', 'El catálogo administrativo no es válido.');
    }
    const snapshot = mapAdminBootstrapResponse(response.body, locationId);
    this.current = snapshot;
    return snapshot;
  }
}



// WEBPACK FOOTER //
// ./src/providers/catalogs/catalogs.ts